import { useCallback, useEffect, useMemo, useState } from 'react';

import Text from '@cartwhl/ui/base/TextNew';

import { getActiveAssetsLoad } from '../assetLibraryAssetsLoad';
import type { AssetLibraryEmbedConfig } from '../assetLibraryEmbedConfig';
import {
  AssetLibraryEmbedProvider,
  useAssetLibraryEmbedConfig,
} from '../assetLibraryEmbedContext';
import { shouldAutoLoadMoreLibraryAssets } from '../assetLibraryFilters';
import {
  getAssetLibraryBodyClassName,
  getAssetLibraryGridClassName,
  getAssetLibrarySkeletonCount,
} from '../assetLibraryGridLayout';
import { getAssetLibraryGridViewState } from '../assetLibraryGridViewState';
import {
  Provider as AssetLibraryProvider,
  useStore,
} from '../assetLibraryStore';
import { AssetLibraryAssetActionsProvider } from '../hooks/useAssetLibraryAssetActions';
import { useAssetLibraryPickHandlers } from '../hooks/useAssetLibraryPickHandlers';
import { useAssetLibraryShareLinkFromUrl } from '../hooks/useAssetLibraryShareLinkFromUrl';
import { useAssetLibraryUrlTypeFilter } from '../hooks/useAssetLibraryUrlTypeFilter';
import { useAutoLoadAssetsWhenFiltered } from '../hooks/useAutoLoadAssetsWhenFiltered';
import { filterAssetsForDisplay } from '../utils';
import { getAssetLibraryItemKey } from '../utils';
import AssetLibraryAssetsGrid from './AssetLibraryAssetsGrid';
import './AssetLibraryCard.css';
import AssetLibraryEmbeddedHeader from './AssetLibraryEmbeddedHeader';
import AssetLibraryFilters from './AssetLibraryFilters';
import AssetLibraryGroupDetail from './AssetLibraryGroupDetail';
import AssetLibraryJobPreviewModals from './AssetLibraryJobPreviewModals';
import AssetLibraryLoading from './AssetLibraryLoading';
import AssetLibraryMediaPickerFooter from './AssetLibraryMediaPickerFooter';
import { AssetLibraryConnectedMediaPreviewModal } from './AssetLibraryMediaPreviewModal';
import AssetLibraryNoResultsMessage from './AssetLibraryNoResultsMessage';
import AssetLibraryPageHeader from './AssetLibraryPageHeader';
import AssetLibraryStockMotionPreviewModal from './AssetLibraryStockMotionPreviewModal';

export interface LibraryPageContentProps {
  /** When true, render inside a modal shell (Cartographer / Motion Editor). */
  embedded?: boolean;
  /** Embedded app context (asset type lock, pick mode, type filter subset). */
  embedConfig?: AssetLibraryEmbedConfig | null;
}

export default function LibraryPageContent({
  embedded = false,
  embedConfig = null,
}: LibraryPageContentProps) {
  const resolvedEmbedConfig = embedded ? embedConfig : null;

  return (
    <AssetLibraryProvider>
      <AssetLibraryEmbedProvider
        config={resolvedEmbedConfig}
        compact={embedded}
      >
        <AssetLibraryAssetActionsProvider>
          {!resolvedEmbedConfig?.pickMode ? (
            <>
              <AssetLibraryJobPreviewModals />
              <AssetLibraryStockMotionPreviewModal />
              <AssetLibraryConnectedMediaPreviewModal />
            </>
          ) : null}
          {embedded ? (
            <div className="bg-primary flex min-h-0 flex-1 flex-col rounded-2xl">
              <AssetLibraryEmbeddedHeader
                titleSuffix={resolvedEmbedConfig?.titleSuffix}
              />
              <LibraryAssetsBody embedded />
              {resolvedEmbedConfig?.mediaSelection ? (
                <AssetLibraryMediaPickerFooter />
              ) : null}
            </div>
          ) : (
            <section className="mx-auto flex w-full max-w-(--breakpoint-lg) flex-col gap-8">
              <AssetLibraryPageHeader />
              <div className="bg-primary flex min-h-[400px] w-full flex-col rounded-2xl">
                <LibraryAssetsBody embedded={embedded} />
              </div>
            </section>
          )}
        </AssetLibraryAssetActionsProvider>
      </AssetLibraryEmbedProvider>
    </AssetLibraryProvider>
  );
}

function LibraryAssetsBody({ embedded = false }: { embedded?: boolean }) {
  const store = useStore();
  useAssetLibraryUrlTypeFilter(!embedded);
  useAssetLibraryShareLinkFromUrl(!embedded);
  const embedConfig = useAssetLibraryEmbedConfig();
  const {
    pickMode,
    bulkSelectionEnabled,
    handlePickMogenJob,
    getAssetClickHandler,
    handleStockMotionLibraryClick,
    isAssetSelected,
    onToggleAssetSelection,
  } = useAssetLibraryPickHandlers();
  const {
    filters,
    isLoadingMore,
    libraryView,
    motionLibraryLoad,
    selectedGroup,
    sortAscending,
    viewMode,
  } = store.state;
  const assetsLoad = getActiveAssetsLoad(store.state);

  const assets = assetsLoad.data?.items ?? [];
  const displayAssets = filterAssetsForDisplay(
    assets,
    filters,
    embedConfig,
    sortAscending,
    libraryView,
  );
  const bulkSelectableKeys = useMemo(
    () => displayAssets.map(getAssetLibraryItemKey),
    [displayAssets],
  );
  const bulkSelectableKeysKey = bulkSelectableKeys.join('|');
  const gridClassName = getAssetLibraryGridClassName(embedded, viewMode);
  const skeletonCount = getAssetLibrarySkeletonCount(embedded);
  const bodyClassName = getAssetLibraryBodyClassName(embedded);
  const motionLibraryMode = store.getMotionLibraryMode();
  const assetsExhausted =
    assetsLoad.status === 'ready' && !assetsLoad.data?.nextToken;
  const hasMoreAssets =
    assetsLoad.status === 'ready' && Boolean(assetsLoad.data?.nextToken);
  const gridView = getAssetLibraryGridViewState({
    displayAssets,
    motionLibraryMode,
    motionLibraryLoad,
    assetsExhausted,
    hasMoreAssets,
    isLoadingMore,
  });

  const handleLoadMore = useCallback(() => {
    void store.loadMoreContent();
  }, [store]);

  const [scrollRoot, setScrollRoot] = useState<HTMLDivElement | null>(null);
  const setScrollContainerNode = useCallback((node: HTMLDivElement | null) => {
    setScrollRoot(node);
  }, []);

  const filterAutoLoadKey = [
    libraryView,
    filters.typeFilter,
    filters.lastModifiedFilter,
    filters.createdByUser,
    filters.searchQuery,
    sortAscending,
  ].join('|');

  useAutoLoadAssetsWhenFiltered({
    container: scrollRoot,
    embedded,
    enabled:
      shouldAutoLoadMoreLibraryAssets(filters, libraryView) && hasMoreAssets,
    isLoading: isLoadingMore,
    onLoadMore: handleLoadMore,
    contentVersion: displayAssets.length + assets.length,
    filterKey: filterAutoLoadKey,
  });

  useEffect(() => {
    if (!bulkSelectionEnabled) {
      return;
    }

    store.pruneAssetSelectionKeys(bulkSelectableKeys);
  }, [bulkSelectableKeys, bulkSelectableKeysKey, bulkSelectionEnabled, store]);

  if (selectedGroup) {
    return (
      <AssetLibraryGroupDetail
        embedded={embedded}
        pickMode={pickMode}
        onPickMogenJob={pickMode ? handlePickMogenJob : undefined}
      />
    );
  }

  const filtersSection = <AssetLibraryFilters embedded={embedded} />;
  const isInitialLoading =
    assetsLoad.status === 'initial' ||
    (assetsLoad.status === 'pending' && assets.length === 0);

  if (isInitialLoading) {
    return (
      <div ref={setScrollContainerNode} className={bodyClassName}>
        {filtersSection}
        <AssetLibraryLoading
          embedded={embedded}
          gridClassName={gridClassName}
        />
      </div>
    );
  }

  if (assetsLoad.status === 'error' && assets.length === 0) {
    return (
      <div ref={setScrollContainerNode} className={bodyClassName}>
        {filtersSection}
        <div className="flex flex-1 items-center justify-center p-8">
          <Text
            token="14/medium"
            className="text-secondary text-center text-pretty"
          >
            {assetsLoad.error ?? 'Unable to load library assets.'}
          </Text>
        </div>
      </div>
    );
  }

  if (!gridView.hasGridContent) {
    return (
      <div ref={setScrollContainerNode} className={bodyClassName}>
        {filtersSection}
        {gridView.isMotionLibraryLoading ? (
          <AssetLibraryLoading
            embedded={embedded}
            gridClassName={gridClassName}
          />
        ) : (
          <div className="flex flex-1 items-center justify-center p-8">
            {motionLibraryLoad.status === 'error' ? (
              <Text
                token="14/medium"
                className="text-secondary pt-8 text-center text-pretty"
              >
                {motionLibraryLoad.error ?? 'Unable to load motion library.'}
              </Text>
            ) : (
              <div className="pt-8">
                <AssetLibraryNoResultsMessage />
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div ref={setScrollContainerNode} className={bodyClassName}>
      {filtersSection}
      <AssetLibraryAssetsGrid
        embedded={embedded}
        gridClassName={gridClassName}
        cardLayout={viewMode}
        skeletonCount={skeletonCount}
        gridView={gridView}
        displayAssets={displayAssets}
        motionLibraryItems={motionLibraryLoad.items}
        motionLibraryError={motionLibraryLoad.error}
        scrollRoot={scrollRoot}
        getAssetClickHandler={getAssetClickHandler}
        bulkSelectionEnabled={bulkSelectionEnabled}
        isAssetSelected={isAssetSelected}
        onToggleAssetSelection={onToggleAssetSelection}
        onStockMotionClick={handleStockMotionLibraryClick}
        onLoadMore={handleLoadMore}
      />
    </div>
  );
}
